import React from 'react';
import { useParams } from 'react-router-dom';

import { makeStyles, withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Hidden from '@material-ui/core/Hidden';
import AddShoppingCartIcon from '@material-ui/icons/AddShoppingCart';
import ShoppingCartIcon from '@material-ui/icons/ShoppingCart';

import { useShoppingCartContext } from '@/contexts/useShoppingCartContext';
import config from '@/configs/config';
import firebase from '@/services/firebase';
import useFirebaseDatabaseQuery from '@/hooks/useFirebaseDatabaseQuery';
import helper from '@/helper';

import Carousel from '@/components/Carousel';
import TabPanel from '@/components/TabPanel';
import Alert from '@/components/Alert';
import ProgressBackdrop from '@/components/ProgressBackdrop';
import Header from '@/containers/Layout/Header';

import Introduction from './Introduction';
import Rule from './Rule';
import QA from './QA';

import CreateOrder from './modals/CreateOrder';

const CREATE_ORDER_POPUP = 'CREATE_ORDER_POPUP';
const COPIED_ALERT = 'COPIED_ALERT';

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: 'calc(100vh - 100px)',
  },
  searchIconRoot: {
    padding: 0,
  },
  stepSection: {
    backgroundColor: theme.palette.grey[200],
  },
  stepper: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    paddingLeft: 0,
    paddingRight: 0,
    backgroundColor: theme.palette.grey[200],
  },
  normalLabel: {
    backgroundColor: theme.palette.success.main,
  },
  almostExpiredLabel: {
    backgroundColor: theme.palette.warning.main,
  },
  productSlogan: {
    fontSize: '1rem',
    fontWeight: 'bold',
  },
  productName: {
    fontSize: '1.2rem',
    fontWeight: 'bold',
  },
  productSpec: {
    color: 'rgb(170, 170, 170)',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
}));

const StyledTabs = withWidth()(withStyles({
  indicator: {
    display: 'none',
  },
})((props) => (
  <Tabs
    {...props}
    {...(props.width === 'xs' ? { variant: 'fullWidth' } : {})}
    TabIndicatorProps={{ children: <span /> }}
  />
))); // eslint-disable-line

const StyledTab = withStyles((theme) => ({
  root: {
    color: theme.palette.common.black,
    fontWeight: theme.typography.fontWeightBold,
    fontSize: theme.typography.pxToRem(16),
    minWidth: 'auto',
  },
  selected: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.common.black,
  },
}))((props) => <Tab disableRipple {...props} />); // eslint-disable-line

const TAB_MAP = {
  introduction: 1,
  rule: 2,
  qa: 4,
};

const Detail = () => {
  const classes = useStyles();
  const { preOrderId } = useParams();

  const now = new Date().getTime();
  const [activeAlert, setActiveAlert] = React.useState(null);
  const [activateTab, setActivateTab] = React.useState(TAB_MAP.introduction);
  const [activatePopup, setActivatePopup] = React.useState(null);

  const {
    shoppingCart,
    addPreOrderToShoppingCart,
  } = useShoppingCartContext();
  const isPreOrderInShoppingCart = !!shoppingCart[preOrderId];

  const dataRef = firebase.database().ref(`/${process.env.REACT_APP_MODE}/pre-order/preOrderEvents/${preOrderId}`);
  const { isLoading, data: preOrder } = useFirebaseDatabaseQuery(dataRef);

  const basicSalePrice = helper.getBasePrice(preOrder);

  const previewImages = React.useMemo(() => {
    if (!preOrder) return {};
    return preOrder.images?.split(',').map((image) => ({
      imageUrl: `${config.RESOURCE_URI}${image}`,
    }));
  }, [preOrder]);

  const handleTabChange = (event, newActivateTab) => {
    setActivateTab(newActivateTab);
  };

  const handleOpenCheckoutPopup = React.useCallback(() => {
    setActivatePopup(CREATE_ORDER_POPUP);
  }, []);

  const handleCloseCheckoutPopup = React.useCallback(() => {
    setActivatePopup(null);
  }, []);

  // NOTE: Section UIs
  const infoSection = (
    <>
      <Hidden only="xs">
        <Box my={2} px={1}>
          <Typography className={classes.productSlogan}>{preOrder?.slogan}</Typography>
          <Typography className={classes.productName}>{preOrder?.title}</Typography>
          <Typography className={classes.productSpec}>{preOrder?.spec}</Typography>
        </Box>
        <Divider />
        <Box my={2} px={1} color="rgb(97, 97, 97)" fontWeight="fontWeightBold">
          <Grid container alignItems="center" spacing={2}>
            <Grid item style={{ textDecoration: 'line-through' }}>
              原價&nbsp;
              {preOrder?.priceOriginal}
            </Grid>
            <Grid item>
              優惠價&nbsp;
              <span style={{ color: 'rgb(176, 43, 43)' }}>{`$${basicSalePrice.toString() || ''}`}</span>
            </Grid>
          </Grid>
        </Box>
        <Divider />
        {
          preOrder?.registerStartTime < now
          && preOrder?.registerEndTime > now
          && (
            <CreateOrder />
          )
        }
      </Hidden>
    </>
  );
  const headSection = (
    <>
      <Hidden only="xs">
        <Box my={2}>
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <Carousel data={previewImages} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box>
                {infoSection}
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Hidden>
      <Hidden only="sm">
        <Grid container>
          <Grid item xs={12} sm={6}>
            <Carousel data={previewImages} />
          </Grid>
          <Grid item xs={12} sm={6}>
            {infoSection}
          </Grid>
        </Grid>
      </Hidden>
    </>
  );
  const tabsSection = (
    <StyledTabs value={activateTab} onChange={handleTabChange}>
      <StyledTab label="商品介紹" value={TAB_MAP.introduction} />
      <StyledTab label="預購資訊" value={TAB_MAP.rule} />
      <StyledTab label="常見問答" value={TAB_MAP.qa} />
    </StyledTabs>
  );
  const contentSection = !isLoading && (
    <Paper>
      <Box px={3} py={4} pb={{ xs: 7, sm: 3 }} minHeight={400}>
        <TabPanel id={TAB_MAP.introduction} activateId={activateTab}>
          <Introduction data={preOrder} />
        </TabPanel>
        <TabPanel id={TAB_MAP.rule} activateId={activateTab}>
          <Rule />
        </TabPanel>
        <TabPanel id={TAB_MAP.qa} activateId={activateTab}>
          <QA />
        </TabPanel>
      </Box>
    </Paper>
  );
  const actionsSection = (
    preOrder?.registerStartTime < now
     && preOrder?.registerEndTime > now
  ) && (
    <Hidden only="sm">
      <Box
        width={1}
        position="fixed"
        left={0}
        bottom={0}
        bgcolor="grey.100"
        style={{
          paddingBottom: 'env(safe-area-inset-bottom, 0)',
        }}
      >
        <Box mx="auto">
          <Grid container alignItems="center">
            <Grid item xs={1} style={{ textAlign: 'center' }}>
              {
                isPreOrderInShoppingCart ? <ShoppingCartIcon style={{ opacity: 0.3 }} /> : (
                  <AddShoppingCartIcon
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                      if (!isPreOrderInShoppingCart) {
                        addPreOrderToShoppingCart({ preOrderId });
                      }
                    }}
                  />
                )
              }
            </Grid>
            <Grid item xs={4}>
              <Grid container alignItems="center">
                <Grid item xs={6}>
                  <Box textAlign="right">
                    <Typography variant="caption" align="right">優惠價&nbsp;</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="error" variant="body1">
                    {`$${basicSalePrice.toString() || ''}`}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleOpenCheckoutPopup}
              >
                <Box py={1} fontSize="body1.fontSize" fontWeight="fontWeightBold">
                  預購
                </Box>
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Hidden>
  );
  const alertSection = (
    <>
      {
        activeAlert === COPIED_ALERT && (
          <Alert
            isOpen
            title="分享網址複製成功"
            content="現在立刻打開對話視窗，將網址貼上通知好友！"
            buttons={(
              <Button fullWidth onClick={() => setActiveAlert(null)}>
                <Box fontWeight="fontWeightBold" color="support.main">
                  確認
                </Box>
              </Button>
            )}
          />
        )
      }
    </>
  );
  const modalSection = (
    <>
      <Hidden only="sm">
        {
          activatePopup === CREATE_ORDER_POPUP && (
            <CreateOrder
              handleClose={handleCloseCheckoutPopup}
            />
          )
        }
      </Hidden>
    </>
  );
  return (
    <>
      <Header />
      <Box className={[classes.root, 'preorder-detail'].join(' ')}>
        {headSection}
        {tabsSection}
        {contentSection}
        {actionsSection}
        {alertSection}
        {modalSection}
        {isLoading && <ProgressBackdrop isShow />}
      </Box>
    </>
  );
};

export default Detail;
